"use client";
import "@/styles/page/lich-su/_mission-vision.scss";

type MissionVisionItem = {
  label?: string;
  description?: string;
};

type MissionVisionCard = {
  title?: string;
  intro?: string;
  sectionTitle?: string;
  items?: Array<string | MissionVisionItem>;
};

export type LichSuMissionVisionProps = {
  title?: string;
  subtitle?: string;
  mission?: MissionVisionCard;
  vision?: MissionVisionCard;
};

const DEFAULT_DATA: Required<LichSuMissionVisionProps> = {
  title: "TẦM NHÌN - SỨ MỆNH",
  subtitle:
    "Đại học Ngân hàng hướng đến mục tiêu hiện thực hóa tầm nhìn và sứ mệnh theo định hướng đề ra, góp phần xây dựng một trường đại học uy tín trong khu vực và trường quốc tế.",
  mission: {
    title: "sứ mệnh",
    intro:
      "HUB cung cấp cho xã hội và ngành ngân hàng nguồn nhân lực chất lượng cao, các nghiên cứu có tầm ảnh hưởng, cùng với dịch vụ tư vấn và hoạt động phục vụ cộng đồng. HUB kiến tạo hệ sinh thái giáo dục, mang đến cơ hội học tập suốt đời, phát triển con người toàn diện, sáng tạo, với tinh thần phụng sự.",
    sectionTitle: "Hệ giá trị cốt lõi",
    items: [
      {
        label: "Chính trực",
        description:
          "Chính trực trong mọi hành động. HUB luôn nhất quán giữa Tư duy - Lời nói - Hành động.",
      },
      {
        label: "Đoàn kết",
        description:
          "Đoàn kết tạo nên sự thống nhất để có sức mạnh tổng hợp. HUB đảm bảo sự hài hòa lợi ích giữa các bên có liên quan để cùng nhau phát triển.",
      },
      {
        label: "Tiên phong",
        description:
          "HUB tiên phong trong ứng dụng thành tựu khoa học công nghệ vào đào tạo, nghiên cứu, quản lý, điều hành.",
      },
    ],
  },
  vision: {
    title: "tầm nhìn",
    intro:
      "HUB định hướng trở thành đại học đa ngành và liên ngành nằm trong nhóm các đại học có uy tín ở khu vực Đông Nam Á. HUB tiên phong ứng dụng công nghệ số trong đào tạo, nghiên cứu và giải quyết các vấn đề liên ngành.",
    sectionTitle: "Triết lý giáo dục",
    items: [
      {
        label: "Khai phóng",
        description:
          "HUB tạo môi trường giúp người học tự khai phá tiềm năng; lĩnh hội kiến thức chuyên môn trên nền tảng kiến thức tổng quát; phát triển năng lực trí tuệ, kỹ năng và giá trị sống tích cực.",
      },
      {
        label: "Liên ngành",
        description:
          "Đào tạo người học có hiểu biết liên ngành nhằm tăng khả năng kết nối, mở rộng cơ hội việc làm.",
      },
      {
        label: "Trải nghiệm",
        description:
          "Mô hình đào tạo 'trưởng thành qua trải nghiệm' - qua trải nghiệm, người học hiểu sâu lý thuyết, hình thành tư duy thực tiễn và năng lực thực thi.",
      },
    ],
  },
};

const normalizeItem = (item: string | MissionVisionItem): MissionVisionItem | null => {
  if (typeof item === "string") {
    const raw = item.trim();
    if (!raw) return null;
    const splitIndex = raw.indexOf(":");
    if (splitIndex < 0) return { label: raw, description: "" };
    return {
      label: raw.slice(0, splitIndex).trim(),
      description: raw.slice(splitIndex + 1).trim(),
    };
  }
  if (!item || typeof item !== "object") return null;
  return {
    label: typeof item.label === "string" ? item.label : "",
    description: typeof item.description === "string" ? item.description : "",
  };
};

const resolveCard = (
  card: MissionVisionCard | undefined,
  fallback: Required<LichSuMissionVisionProps>["mission"]
) => {
  const fallbackItems = Array.isArray(fallback.items)
    ? fallback.items.map(normalizeItem).filter((item): item is MissionVisionItem => item !== null)
    : [];
  const items = Array.isArray(card?.items)
    ? card.items.map(normalizeItem).filter((item): item is MissionVisionItem => item !== null)
    : fallbackItems;
  return {
    title: card?.title || fallback.title,
    intro: card?.intro || fallback.intro,
    sectionTitle: card?.sectionTitle || fallback.sectionTitle,
    items: items.length > 0 ? items : fallbackItems,
  };
};

export default function LichSuMissionVision(props: LichSuMissionVisionProps) {
  const title = props.title || DEFAULT_DATA.title;
  const subtitle = props.subtitle || DEFAULT_DATA.subtitle;
  const mission = resolveCard(props.mission, DEFAULT_DATA.mission);
  const vision = resolveCard(props.vision, DEFAULT_DATA.vision);

  return (
    <section className="mission-vision">
      <div className="container">
        <div className="mission-vision__header">
          <h2 className="mission-vision__title">{title}</h2>
          <p className="mission-vision__subtitle">{subtitle}</p>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-6">
            <article className="mission-card">
              <h3 className="mission-card__title">{mission.title}</h3>
              <p className="mission-card__intro">{mission.intro}</p>
              <h4 className="mission-card__subtitle">{mission.sectionTitle}</h4>
              <ul className="mission-card__list">
                {mission.items.map((item, index) => (
                  <li key={`mission-${index}`}>
                    <strong>{item.label}:</strong> {item.description}
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="col-12 col-lg-6">
            <article className="mission-card mission-card--vision">
              <h3 className="mission-card__title">{vision.title}</h3>
              <p className="mission-card__intro">{vision.intro}</p>
              <h4 className="mission-card__subtitle">{vision.sectionTitle}</h4>
              <ul className="mission-card__list">
                {vision.items.map((item, index) => (
                  <li key={`vision-${index}`}>
                    <strong>{item.label}:</strong> {item.description}
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

